import { Fragment, useMemo } from 'react';
import { Table, List, Panel, Loader } from 'rsuite';
import { v4 as uuidv4 } from 'uuid';
import { BrowserView, MobileView } from 'react-device-detect';
import { useTable, useFlexLayout } from 'react-table'
import useLocalStorage from 'lib/hooks/useLocalStorage';
import omit from 'lodash/omit';

const ResponsiveTable = (props: any) => {
  const [visibleColumns] = useLocalStorage(props?.filterKey + 'Columns', []);

  const columnVisible = (key: string) => {
    return true;
  }

  const mobileChildren = (Array.isArray(props.children) ? props.children.filter((c: any) => c) : [props.children.filter((c: any) => c)])
    .filter((t: any) => columnVisible(t.props.children[1].props.column))

  // everything in rsuite tables needs header and cell
  // child 0 - header
  // child 1 - cell
  const tableChildren = mobileChildren.filter((c: any) => c?.props?.children.length > 1);

  const columns: any = useMemo(() => tableChildren.map((t: any) => {
    if (Array.isArray(t.props.children) && typeof (t.props.children[0].props.children) === 'string') {
      return {
        ...t.props,
        Header: t.props.children[0].props.children,
        id: uuidv4(),
        width: t.props.width,
        className: t.props.children[1].props.className || '',
        dataKey: t.props.children[1].props.dataKey
      };
    } else {
      return (props.data.length === 0 || props.loading)
        ? undefined
        : {
          ...t.props,
          id: uuidv4(),
          Header: () => t.props.children[0].props.children,
          width: t.props.width,
          className: t.props.children[1].props.className || '',
          dataKey: t.props.children[1].props.dataKey
        }
    }
  }).filter((t: any) => t), [props.selectedTableRows, props.data, props.loading]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable(
    {
      columns,
      data: props.data
    },
    useFlexLayout
  );

  return <Fragment>
    <BrowserView>
      {props.html5
        ? <table className="html5" {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <th {...column.getHeaderProps({ className: column.className })}>
                    {column.sortable
                      ? <div className="rs-table-cell-header rs-table-cell-header-sortable" style={{ cursor: 'pointer' }} onClick={(e: any) => {
                        e.preventDefault();
                        props.onSortColumn(column.dataKey);
                      }}>
                        {column.render('Header')}
                        <span className="rs-table-cell-header-sort-wrapper">
                          <i className={`rs-table-cell-header-icon-sort rs-table-cell-header-icon-sort-${props.sortColumn === column.dataKey ? props.sortType : ''}`}></i>
                        </span>
                      </div>
                      : <span>{column.render('Header')}</span>
                    }
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(rows.length === 0 || props.loading)
              ? <tr>
                <td className="text-center no-hover">
                  {props.loading ? <Loader content="Loading..." /> : <div className="p-10">No data found</div>}
                </td>
              </tr>
              : rows.map((row: any) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells
                      .map((cell: any, index: number) => {
                        const c = mobileChildren[index];

                        return <td {...cell.getCellProps({ className: cell.column.className })}>
                          {c.props.children[1].props.children
                            ? c.props.children[1].props.children(row.original)
                            : row.original[c.props.children[1].props.dataKey]
                          }
                        </td>
                      })}
                  </tr>
                )
              }
              )}
          </tbody>
        </table>
        : <Table {...omit(props, 'filterKey')} autoHeight rowHeight={40}>
          {props.children.filter((t: any) => t && columnVisible(t.props.children[1].props.column))}
        </Table>
      }
    </BrowserView>
    <MobileView>
      {props.loading
        ? <Loader content="Loading..." />
        : <Fragment>
          {props.data.map((d: any, index: number) =>
            <Panel
              key={d.guid + '-' + index}
              className={'mb-12 pb-0 card'}
              style={{ backgroundColor: '#fff' }}
            >
              <List size="sm">
                {mobileChildren.map((c: any) => c &&
                  <List.Item key={uuidv4()} className="pl-10 pr-10">
                    {c.props.children[0].props.children.length > 1 && <strong>{c.props.children[0].props.children}:&nbsp;</strong>}
                    {c.props.children[1].props.children
                      ? <Fragment>{c.props.children[1].props.children(d)}</Fragment>
                      : <Fragment>{props.data[index][c.props.children[1].props.dataKey]}</Fragment>
                    }
                  </List.Item>
                )}
              </List>
            </Panel>
          )}
        </Fragment>
      }
    </MobileView>
  </Fragment >;
};

export default ResponsiveTable;

